/**
 * MAVLink Command Definitions
 *
 * Based on ArduPilot MAV_CMD specifications
 * Reference: https://ardupilot.org/copter/docs/common-mavlink-mission-command-messages-mav_cmd.html
 */

import { VehicleType } from '@/features/mission/types/mission.types';

/**
 * MAVLink Command Types (MAV_CMD enum)
 */
export enum MavCommand {
  // Navigation Commands (16-95)
  NAV_WAYPOINT = 16,             // Navigate to waypoint
  NAV_LOITER_UNLIM = 17,         // Loiter unlimited time
  NAV_LOITER_TURNS = 18,         // Loiter N turns
  NAV_LOITER_TIME = 19,          // Loiter for specified time
  NAV_RETURN_TO_LAUNCH = 20,     // Return to launch position
  NAV_LAND = 21,                 // Land at location
  NAV_TAKEOFF = 22,              // Takeoff from ground
  NAV_LAND_LOCAL = 23,           // Land at local position
  NAV_TAKEOFF_LOCAL = 24,        // Takeoff from local position
  NAV_FOLLOW = 25,               // Follow target
  NAV_CONTINUE_AND_CHANGE_ALT = 30, // Continue on current course and change altitude
  NAV_LOITER_TO_ALT = 31,        // Loiter to altitude
  NAV_ROI = 80,                  // Region of Interest
  NAV_PATHPLANNING = 81,         // Control path planning
  NAV_SPLINE_WAYPOINT = 82,      // Navigate to waypoint using spline path
  NAV_VTOL_TAKEOFF = 84,         // VTOL takeoff
  NAV_VTOL_LAND = 85,            // VTOL land
  NAV_GUIDED_ENABLE = 92,        // Enable guided mode
  NAV_DELAY = 93,                // Delay next navigation command
  NAV_PAYLOAD_PLACE = 94,        // Place payload
  NAV_LAST = 95,                 // Marker for last navigation command

  // Condition Commands (112-159)
  CONDITION_DELAY = 112,         // Delay mission state machine
  CONDITION_CHANGE_ALT = 113,    // Ascend/descend to altitude
  CONDITION_DISTANCE = 114,      // Delay command until within distance
  CONDITION_YAW = 115,           // Reach target heading
  CONDITION_LAST = 159,          // Marker for last condition command

  // DO Commands (176-255)
  DO_SET_MODE = 176,             // Set flight mode
  DO_JUMP = 177,                 // Jump to command index
  DO_CHANGE_SPEED = 178,         // Change speed
  DO_SET_HOME = 179,             // Set home position
  DO_SET_PARAMETER = 180,        // Set parameter value
  DO_SET_RELAY = 181,            // Set relay state
  DO_REPEAT_RELAY = 182,         // Cycle relay on/off
  DO_SET_SERVO = 183,            // Set servo PWM
  DO_REPEAT_SERVO = 184,         // Cycle servo
  DO_FLIGHTTERMINATION = 185,    // Terminate flight
  DO_CHANGE_ALTITUDE = 186,      // Change altitude
  DO_LAND_START = 189,           // Mission item marking start of landing
  DO_RALLY_LAND = 190,           // Land at nearest rally point
  DO_GO_AROUND = 191,            // Abort landing and go around
  DO_REPOSITION = 192,           // Reposition vehicle
  DO_PAUSE_CONTINUE = 193,       // Pause/continue mission
  DO_SET_REVERSE = 194,          // Set reverse mode (rover)
  DO_SET_ROI_LOCATION = 195,     // Set region of interest (location)
  DO_SET_ROI_WPNEXT_OFFSET = 196, // Set ROI to next waypoint with offset
  DO_SET_ROI_NONE = 197,         // Cancel any ROI
  DO_CONTROL_VIDEO = 200,        // Control onboard camera
  DO_SET_ROI = 201,              // Set region of interest (deprecated)
  DO_DIGICAM_CONFIGURE = 202,    // Configure camera
  DO_DIGICAM_CONTROL = 203,      // Control camera
  DO_MOUNT_CONFIGURE = 204,      // Configure mount
  DO_MOUNT_CONTROL = 205,        // Control camera mount
  DO_SET_CAM_TRIGG_DIST = 206,   // Set camera trigger distance
  DO_FENCE_ENABLE = 207,         // Enable/disable geofence
  DO_PARACHUTE = 208,            // Parachute release
  DO_MOTOR_TEST = 209,           // Motor test
  DO_INVERTED_FLIGHT = 210,      // Enable inverted flight
  DO_GRIPPER = 211,              // Operate gripper
  DO_AUTOTUNE_ENABLE = 212,      // Enable autotune
  DO_SET_CAM_TRIGG_INTERVAL = 214, // Set camera trigger interval
  DO_MOUNT_CONTROL_QUAT = 220,   // Control mount with quaternion
  DO_GUIDED_MASTER = 221,        // Set guided master system
  DO_GUIDED_LIMITS = 222,        // Set guided mode limits
  DO_ENGINE_CONTROL = 223,       // Control engine
  DO_SET_MISSION_CURRENT = 224,  // Set current mission item
  DO_LAST = 240,                 // Marker for last DO command

  // Camera Commands
  IMAGE_START_CAPTURE = 2000,    // Start image capture sequence
  IMAGE_STOP_CAPTURE = 2001,     // Stop image capture sequence
  VIDEO_START_CAPTURE = 2500,    // Start video capture
  VIDEO_STOP_CAPTURE = 2501,     // Stop video capture
}

/**
 * Command parameter definition
 */
export interface MavCommandParam {
  index: 1 | 2 | 3 | 4;          // Parameter index (param1-4)
  name: string;                  // Parameter name
  description: string;           // What this parameter does
  unit?: string;                  // Unit (e.g., 'meters', 'seconds', 'degrees')
  min?: number;                  // Minimum valid value
  max?: number;                  // Maximum valid value
  default: number;               // Default value
}

/**
 * Command metadata for UI display
 */
export interface MavCommandInfo {
  id: MavCommand;
  name: string;
  category: 'navigation' | 'condition' | 'do' | 'camera';
  description: string;
  params: MavCommandParam[];
  supportedVehicles: VehicleType[];
}

/**
 * Commonly used commands for quick access
 */
export const COMMON_COMMANDS: MavCommand[] = [
  MavCommand.NAV_WAYPOINT,
  MavCommand.NAV_TAKEOFF,
  MavCommand.NAV_LAND,
  MavCommand.NAV_RETURN_TO_LAUNCH,
  MavCommand.NAV_LOITER_TIME,
  MavCommand.NAV_SPLINE_WAYPOINT,
  MavCommand.DO_CHANGE_SPEED,
  MavCommand.DO_SET_ROI_LOCATION,
];

/**
 * Command information database
 * Contains metadata for all supported commands
 */
export const COMMAND_INFO: Partial<Record<MavCommand, MavCommandInfo>> = {
  [MavCommand.NAV_WAYPOINT]: {
    id: MavCommand.NAV_WAYPOINT,
    name: 'Waypoint',
    category: 'navigation',
    description: 'Navigate to waypoint position and hold for specified time',
    params: [
      {
        index: 1,
        name: 'Hold',
        description: 'Hold time at waypoint',
        unit: 'seconds',
        min: 0,
        default: 0,
      },
      {
        index: 2,
        name: 'Accept Radius',
        description: 'Acceptance radius',
        unit: 'meters',
        min: 0,
        default: 3,
      },
      {
        index: 3,
        name: 'Pass Radius',
        description: '0 to pass through WP, >0 radius to pass by WP',
        unit: 'meters',
        default: 0,
      },
      {
        index: 4,
        name: 'Yaw',
        description: 'Desired yaw angle at waypoint',
        unit: 'degrees',
        min: -180,
        max: 180,
        default: 0,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.PLANE, VehicleType.ROVER, VehicleType.VTOL],
  },

  [MavCommand.NAV_TAKEOFF]: {
    id: MavCommand.NAV_TAKEOFF,
    name: 'Takeoff',
    category: 'navigation',
    description: 'Takeoff from ground and climb to specified altitude',
    params: [
      {
        index: 1,
        name: 'Pitch',
        description: 'Minimum pitch (plane only)',
        unit: 'degrees',
        default: 0,
      },
      {
        index: 2,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 3,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 4,
        name: 'Yaw',
        description: 'Yaw angle',
        unit: 'degrees',
        min: -180,
        max: 180,
        default: 0,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.PLANE, VehicleType.VTOL],
  },

  [MavCommand.NAV_LAND]: {
    id: MavCommand.NAV_LAND,
    name: 'Land',
    category: 'navigation',
    description: 'Land at specified location',
    params: [
      {
        index: 1,
        name: 'Abort Alt',
        description: 'Minimum target altitude if landing is aborted',
        unit: 'meters',
        default: 0,
      },
      {
        index: 2,
        name: 'Land Mode',
        description: 'Precision land mode (0=normal, 1=opportunistic, 2=required)',
        default: 0,
        min: 0,
        max: 2,
      },
      {
        index: 3,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 4,
        name: 'Yaw',
        description: 'Desired yaw angle',
        unit: 'degrees',
        min: -180,
        max: 180,
        default: 0,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.PLANE, VehicleType.VTOL],
  },

  [MavCommand.NAV_RETURN_TO_LAUNCH]: {
    id: MavCommand.NAV_RETURN_TO_LAUNCH,
    name: 'Return to Launch',
    category: 'navigation',
    description: 'Return to home position or nearest rally point',
    params: [
      {
        index: 1,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 2,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 3,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 4,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.PLANE, VehicleType.ROVER, VehicleType.VTOL],
  },

  [MavCommand.NAV_LOITER_TIME]: {
    id: MavCommand.NAV_LOITER_TIME,
    name: 'Loiter Time',
    category: 'navigation',
    description: 'Loiter at position for specified time',
    params: [
      {
        index: 1,
        name: 'Time',
        description: 'Loiter time',
        unit: 'seconds',
        min: 0,
        default: 60,
      },
      {
        index: 2,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 3,
        name: 'Radius',
        description: 'Loiter radius (0=default)',
        unit: 'meters',
        default: 0,
      },
      {
        index: 4,
        name: 'Direction',
        description: 'Loiter direction (1=CW, -1=CCW)',
        default: 1,
        min: -1,
        max: 1,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.PLANE, VehicleType.VTOL],
  },

  [MavCommand.NAV_SPLINE_WAYPOINT]: {
    id: MavCommand.NAV_SPLINE_WAYPOINT,
    name: 'Spline Waypoint',
    category: 'navigation',
    description: 'Navigate to waypoint using smooth spline path',
    params: [
      {
        index: 1,
        name: 'Hold',
        description: 'Hold time',
        unit: 'seconds',
        min: 0,
        default: 0,
      },
      {
        index: 2,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 3,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 4,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.VTOL],
  },

  [MavCommand.DO_CHANGE_SPEED]: {
    id: MavCommand.DO_CHANGE_SPEED,
    name: 'Change Speed',
    category: 'do',
    description: 'Change vehicle speed mid-mission',
    params: [
      {
        index: 1,
        name: 'Speed Type',
        description: '0=Airspeed, 1=Ground speed',
        default: 1,
        min: 0,
        max: 1,
      },
      {
        index: 2,
        name: 'Speed',
        description: 'Target speed',
        unit: 'm/s',
        min: 0,
        default: 5,
      },
      {
        index: 3,
        name: 'Throttle',
        description: 'Throttle (percent, -1=no change)',
        unit: '%',
        min: -1,
        max: 100,
        default: -1,
      },
      {
        index: 4,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.PLANE, VehicleType.ROVER, VehicleType.VTOL],
  },

  [MavCommand.DO_SET_ROI_LOCATION]: {
    id: MavCommand.DO_SET_ROI_LOCATION,
    name: 'Set ROI (Camera)',
    category: 'do',
    description: 'Point camera at specified location',
    params: [
      {
        index: 1,
        name: 'Gimbal Device ID',
        description: 'Component ID of gimbal (0=all)',
        default: 0,
      },
      {
        index: 2,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 3,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
      {
        index: 4,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.PLANE, VehicleType.VTOL],
  },

  [MavCommand.DO_SET_CAM_TRIGG_DIST]: {
    id: MavCommand.DO_SET_CAM_TRIGG_DIST,
    name: 'Camera Trigger Distance',
    category: 'camera',
    description: 'Set camera trigger distance for survey missions',
    params: [
      {
        index: 1,
        name: 'Distance',
        description: 'Distance between shots (0=stop triggering)',
        unit: 'meters',
        min: 0,
        default: 25,
      },
      {
        index: 2,
        name: 'Shutter',
        description: 'Shutter integration time (-1=ignore)',
        unit: 'ms',
        default: -1,
      },
      {
        index: 3,
        name: 'Trigger',
        description: 'Trigger immediately (1=yes)',
        default: 1,
        min: 0,
        max: 1,
      },
      {
        index: 4,
        name: 'Empty',
        description: 'Empty',
        default: 0,
      },
    ],
    supportedVehicles: [VehicleType.COPTER, VehicleType.PLANE, VehicleType.VTOL],
  },
};

/**
 * Get command info by command ID
 */
export function getCommandInfo(command: MavCommand): MavCommandInfo | undefined {
  return COMMAND_INFO[command];
}

/**
 * Get command name by ID
 */
export function getCommandName(command: MavCommand): string {
  const info = COMMAND_INFO[command];
  return info?.name || `Command ${command}`;
}

/**
 * Get commands by category
 */
export function getCommandsByCategory(
  category: 'navigation' | 'condition' | 'do' | 'camera'
): MavCommandInfo[] {
  return Object.values(COMMAND_INFO).filter(
    (info) => info?.category === category
  ) as MavCommandInfo[];
}

/**
 * Check if command is supported for vehicle type
 */
export function isCommandSupportedForVehicle(
  command: MavCommand,
  vehicleType: VehicleType
): boolean {
  const info = COMMAND_INFO[command];
  return info?.supportedVehicles.includes(vehicleType) ?? false;
}
